import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useFilters, type DateRange } from './filters';
import { useVisibilityEngines, useVisibilityTags } from '../../../hooks/useVisibility';
import { useTriggerCitationSweep, useLatestCitationRun } from '../../../hooks/useMeasurement';
import { Button } from '../../../components/ui/Button';
import { SegmentedControl, type TabItem } from '../../../components/ui/Tabs';
import { SweepStatusPill, isRunActive } from './SweepStatusPill';

const RANGE_OPTIONS: readonly TabItem<DateRange>[] = [
  { id: '7d', label: '7 days' },
  { id: '30d', label: '30 days' },
  { id: '90d', label: '90 days' },
  { id: 'all', label: 'All time' },
];

export function FilterBar() {
  const { range, modelId, tagId, setRange, setModelId, setTagId } = useFilters();
  const { data: engines } = useVisibilityEngines();
  const { data: tags } = useVisibilityTags();
  const sweep = useTriggerCitationSweep();
  const latestRun = useLatestCitationRun();
  const run = latestRun.data?.run ?? null;
  const queryClient = useQueryClient();

  // Refetch analytics + visibility once the run flips terminal. Tracked via a
  // ref so the side-effect fires exactly once per transition.
  const lastTerminalRunId = useRef<string | null>(null);
  useEffect(() => {
    if (!run) return;
    const terminal = run.status === 'completed' || run.status === 'completed_with_errors' || run.status === 'failed';
    if (terminal && lastTerminalRunId.current !== run.id) {
      lastTerminalRunId.current = run.id;
      queryClient.invalidateQueries({ queryKey: ['measurement', 'citations'] });
      queryClient.invalidateQueries({ queryKey: ['visibility'] });
    }
  }, [run, queryClient]);

  const runActive = isRunActive(run?.status) || sweep.isPending;

  return (
    <div className="sticky top-0 z-10 -mx-6 px-6 py-3 bg-surface-primary/95 backdrop-blur border-b border-border-light">
      <div className="flex flex-wrap items-center gap-2">
        <SegmentedControl
          ariaLabel="Period"
          options={RANGE_OPTIONS}
          value={range}
          onChange={setRange}
        />
        <Pill>
          <select
            value={modelId ?? ''}
            onChange={(e) => setModelId(e.target.value || null)}
            className="bg-transparent text-body-sm outline-none cursor-pointer pr-1"
          >
            <option value="">All engines</option>
            {(engines?.items ?? []).map((e) => (
              <option key={e.id} value={e.id}>{e.description}</option>
            ))}
          </select>
        </Pill>
        <Pill>
          <select
            value={tagId ?? ''}
            onChange={(e) => setTagId(e.target.value || null)}
            className="bg-transparent text-body-sm outline-none cursor-pointer pr-1"
          >
            <option value="">All tags</option>
            {(tags?.items ?? []).map((t) => (
              <option key={t.id} value={t.id}>{t.name}</option>
            ))}
          </select>
        </Pill>
        <div className="flex-1" />
        <Button
          size="sm"
          variant="primary"
          onClick={() => sweep.mutate()}
          isLoading={sweep.isPending}
          disabled={runActive}
        >
          Run citation check
        </Button>
      </div>
      {(run || sweep.isError) && (
        <div className="mt-2">
          <SweepStatusPill run={run} triggerError={sweep.error} />
        </div>
      )}
    </div>
  );
}

function Pill({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-lg border border-border-light bg-surface-primary px-2.5 py-1 text-body-sm text-text-secondary">
      {children}
    </span>
  );
}
